import axios from 'axios';
import { API_URL } from './config';

const API = API_URL;

// Token management
const TOKEN_KEY = 'fira_auth_token';
const REFRESH_TOKEN_KEY = 'fira_refresh_token';
const USER_KEY = 'fira_user';
const TOKEN_EXPIRY_KEY = 'fira_token_expiry';

export const getStoredToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const getStoredRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const getStoredUser = () => {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

export const getTokenExpiry = () => {
  const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
  return expiry ? parseInt(expiry) : null;
};

export const setAuthData = (token, user, refreshToken = null) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  // Set token expiry (24 hours from now)
  const expiryTime = Date.now() + (24 * 60 * 60 * 1000);
  localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }
  // Set default header for all axios requests
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const clearAuthData = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  delete axios.defaults.headers.common['Authorization'];
};

// Check if token is expired or will expire soon (within 1 hour)
export const isTokenExpiringSoon = () => {
  const expiry = getTokenExpiry();
  if (!expiry) return true; // No expiry set, assume expired
  const oneHourFromNow = Date.now() + (60 * 60 * 1000);
  return expiry < oneHourFromNow;
};

// Check if token is completely expired
export const isTokenExpired = () => {
  const expiry = getTokenExpiry();
  if (!expiry) return true;
  return Date.now() > expiry;
};

// Initialize axios with stored token on app load
export const initializeAuth = () => {
  const token = getStoredToken();
  if (token) {
    // Check if token is expired
    if (isTokenExpired()) {
      console.log('[AUTH] Token expired, clearing auth data');
      clearAuthData();
      return false;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    return true;
  }
  return false;
};

// Auth API calls
export const register = async (email, password, name, phone = null) => {
  console.log('[AUTH] Attempting registration...');
  const response = await axios.post(`${API}/auth/register`, {
    email,
    password,
    name,
    phone
  });
  
  console.log('[AUTH] Registration response:', response.data.success);
  
  if (response.data.success) {
    setAuthData(response.data.access_token, response.data.user);
    console.log('[AUTH] Auth data saved to localStorage');
  }
  
  return response.data;
};

// Register with Invite Code
export const registerWithInvite = async (inviteCode, email, password, name, phone = null) => {
  console.log('[AUTH] Attempting registration with invite code...');
  const response = await axios.post(`${API}/auth/register-with-invite`, {
    invite_code: inviteCode,
    email,
    password,
    name,
    phone
  });
  
  console.log('[AUTH] Invite registration response:', response.data.success);
  
  if (response.data.success) {
    setAuthData(response.data.access_token, response.data.user);
    console.log('[AUTH] Auth data saved to localStorage');
  }
  
  return response.data;
};

// Validate invite code
export const validateInvite = async (code) => {
  const response = await axios.get(`${API}/invite/validate/${code}`);
  return response.data;
};

export const login = async (email, password) => {
  console.log('[AUTH] Attempting login to:', `${API}/auth/login`);
  console.log('[AUTH] API URL:', API);
  
  const maxRetries = 3;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      }, { timeout: 15000 }); // 15s timeout per attempt
      
      console.log('[AUTH] Login response status:', response.status);
      console.log('[AUTH] Login response success:', response.data.success);
      
      if (response.data.success) {
        setAuthData(response.data.access_token, response.data.user, response.data.refresh_token);
        console.log('[AUTH] Auth data saved to localStorage (with refresh token)');
        console.log('[AUTH] Token (first 50 chars):', response.data.access_token?.substring(0, 50));
      }
      
      return response.data;
    } catch (error) {
      console.error(`[AUTH] Login error (attempt ${attempt}/${maxRetries}):`, error.message);
      
      // Retry on 520/502/503/504 or network errors (server busy, not user error)
      const isServerBusy = error.response?.status >= 500 || 
                           error.code === 'ECONNABORTED' || 
                           error.code === 'ERR_NETWORK' ||
                           error.message?.includes('timeout') ||
                           error.message?.includes('Network Error');
      
      if (isServerBusy && attempt < maxRetries) {
        console.log(`[AUTH] Server busy, retrying in ${attempt * 2}s... (${attempt}/${maxRetries})`);
        await new Promise(r => setTimeout(r, attempt * 2000));
        continue;
      }
      
      // Final attempt failed or non-retryable error
      console.error('[AUTH] Error response:', error.response?.data);
      console.error('[AUTH] Error status:', error.response?.status);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return { success: false, error: 'Server lambat respond. Sila cuba lagi.' };
      }
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        return { success: false, error: 'Tidak dapat sambung ke server. Sila semak internet anda.' };
      }
      if (error.response?.status === 401) {
        const detail = error.response?.data?.detail || '';
        return { success: false, error: detail || 'Email atau kata laluan tidak sah.' };
      }
      if (error.response?.status === 403) {
        return { success: false, error: 'Akses akaun ditolak. Sila hubungi admin.' };
      }
      if (error.response?.status === 429) {
        const detail = error.response?.data?.detail || '';
        return { success: false, error: detail || 'Terlalu banyak cubaan. Sila tunggu sebentar.' };
      }
      if (error.response?.status >= 500) {
        return { success: false, error: 'Server sedang sibuk. Sila cuba lagi dalam beberapa saat.' };
      }
      
      return { success: false, error: error.response?.data?.detail || 'Ralat berlaku. Sila cuba lagi.' };
    }
  }
};

export const logout = () => {
  clearAuthData();
  window.location.href = '/';
};

export const getCurrentUser = async () => {
  const response = await axios.get(`${API}/auth/me`);
  return response.data;
};

export const refreshToken = async () => {
  try {
    const storedRefreshToken = getStoredRefreshToken();
    if (!storedRefreshToken) {
      console.log('[AUTH] No refresh token found');
      return false;
    }
    
    const response = await axios.post(`${API}/auth/refresh`, {
      refresh_token: storedRefreshToken
    }, {
      timeout: 30000 // 30 second timeout for refresh
    });
    
    if (response.data.access_token) {
      const user = getStoredUser();
      setAuthData(response.data.access_token, user, response.data.refresh_token);
      console.log('[AUTH] Token refreshed successfully');
      return true;
    }
  } catch (error) {
    console.error('[AUTH] Token refresh failed:', error);
    // Only clear auth if refresh token is invalid/expired
    if (error.response?.status === 401 || error.response?.status === 403) {
      clearAuthData();
    }
  }
  return false;
};

// Check and refresh token if needed (call this on app resume/visibility change)
export const checkAndRefreshToken = async () => {
  if (!getStoredToken()) return false;
  
  // If token is expiring soon, try to refresh
  if (isTokenExpiringSoon()) {
    console.log('[AUTH] Token expiring soon, attempting refresh...');
    return await refreshToken();
  }
  return true;
};

// Setup axios interceptor for auto token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and not already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh token
      const refreshed = await refreshToken();
      if (refreshed) {
        // Retry original request with new token
        originalRequest.headers['Authorization'] = `Bearer ${getStoredToken()}`;
        return axios(originalRequest);
      }
    }
    
    return Promise.reject(error);
  }
);

export const changePassword = async (currentPassword, newPassword) => {
  const response = await axios.post(`${API}/auth/change-password`, {
    current_password: currentPassword,
    new_password: newPassword
  });
  return response.data;
};

// Forgot Password - Request reset link
export const forgotPassword = async (email) => {
  const response = await axios.post(`${API}/auth/forgot-password`, { email });
  return response.data;
};

// Reset Password - Set new password with token
export const resetPassword = async (token, newPassword) => {
  const response = await axios.post(`${API}/auth/reset-password`, {
    token,
    new_password: newPassword
  });
  return response.data;
};

// Verify Reset Token
export const verifyResetToken = async (token) => {
  const response = await axios.get(`${API}/auth/verify-reset-token/${token}`);
  return response.data;
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!getStoredToken();
};
